import type { Event, EventVideoConference, EventWebinar, NodeOrganization } from 'models';
import { useMemo } from 'react';
import { usePermissions } from 'permissions';
import { Form, FormActions, FormLayout, Modal, message } from 'ui';
import {
  AttachmentsFormContent,
  type AttachmentsFormContentValues,
  ChannelFormContent,
  DescriptionImageFormContent,
  DetailFormContent,
  DurationFormContent,
  type ExtendedChannelFormContentValues,
  type ExtendedDescriptionImageFormContentValues,
  type ExtendedDetailFormContentValues,
  type ExtendedDurationFormValues,
} from '../../components';
import { useDeleteEventAttachments, useUploadEventAttachments, useUploadEventCoverPhoto } from '../../hooks';
import { useEditEvent } from '../../queries';
import { useCreateFormRequestData, useGetInitialValues } from './hooks';

export type EditEventFormValues = AttachmentsFormContentValues &
  ExtendedChannelFormContentValues &
  ExtendedDescriptionImageFormContentValues &
  ExtendedDetailFormContentValues &
  ExtendedDurationFormValues;

type P = {
  event: EventVideoConference | EventWebinar;
  onCancel: () => void;
  onSuccess: (eventData: Event) => void;
  organization?: NodeOrganization;
};

const EditEventForm = ({ event, onCancel, onSuccess, organization }: P) => {
  const { isPending, mutate: editEvent } = useEditEvent();
  const { canManageEventChannels } = usePermissions();
  const { isUploadingAttachments, uploadEventAttachments } = useUploadEventAttachments();
  const { isUploadingCoverPhoto, uploadEventCoverPhoto } = useUploadEventCoverPhoto();
  const { deleteEventAttachments, isDeletingAttachments } = useDeleteEventAttachments();
  const initialValues = useGetInitialValues({ event, organization });
  const createFormRequestData = useCreateFormRequestData();

  const onSubmit = async (formValues: EditEventFormValues) => {
    await deleteEventAttachments({ event, formAttachments: formValues.attachments });
    await uploadEventCoverPhoto({
      eventId: event.id,
      existingCoverPhotoId: event.coverPhoto,
      heroImage: formValues.heroImage,
    });
    await uploadEventAttachments({ attachments: formValues.attachments, eventId: event.id });
    editEvent(createFormRequestData(formValues, event.id), {
      onSuccess(eventData) {
        message.success('Event was updated.');
        onSuccess(eventData);
      },
    });
  };

  const isLoading = useMemo(
    () => isPending || isUploadingAttachments || isUploadingCoverPhoto || isDeletingAttachments,
    [isPending, isUploadingAttachments, isUploadingCoverPhoto, isDeletingAttachments],
  );

  return (
    <Form<EditEventFormValues> initialValues={initialValues} onFinish={onSubmit}>
      <FormLayout>
        <DetailFormContent<EditEventFormValues> mode="edit" />
        <DurationFormContent<EditEventFormValues> />
        <DescriptionImageFormContent<EditEventFormValues> />
        {canManageEventChannels({}) ? <ChannelFormContent<EditEventFormValues> /> : null}
        <AttachmentsFormContent<EditEventFormValues> />
      </FormLayout>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            onClick: onCancel,
          }}
          submit={{
            children: 'Edit',
            loading: isLoading,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default EditEventForm;
